/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  DatePicker,
  Row,
  Col,
  Space,
  message,
  Modal,
  Tag,
  Descriptions,
  Form,
  Select,
  Input,
  Image,
} from 'antd';
import { DownloadOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { getTripsParkingReport, updateTripParkingStatus } from '../../../services/ReportsApi';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

export interface DateRange {
  startDate: Dayjs;
  endDate: Dayjs;
}

interface ParkingBillDetails {
  amount: string;
  comment?: string;
}

interface ParkingBillTripProvider {
  firstName: string;
  lastName: string;
  email: string;
}

interface ParkingBillTrip {
  bookingId: string;
  status: string;
  originAddress: string;
  destinationAddress: string;
  startedAt?: Date | string;
  startAt?: Date | string;
  scheduleAt?: Date | string;
  finishedAt: Date;
  provider: ParkingBillTripProvider;
}

interface ParkingBill {
  id: number;
  status: string;
  statusUpdatedBy: string;
  url: string;
  details?: ParkingBillDetails;
  updatedAt: Date;
  trip: ParkingBillTrip;
}

const TripParkingView: React.FC = () => {
  const [parkingBills, setParkingBills] = useState<ParkingBill[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [updating, setUpdating] = useState<number | null>(null);
  const [status, setStatus] = useState<string>('pending');
  const [comment, setComment] = useState<string>('');
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [selectedBill, setSelectedBill] = useState<ParkingBill | null>(null);
  const [selectedAction, setSelectedAction] = useState<'approved' | 'rejected' | ''>('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: dayjs().subtract(1, 'month'),
    endDate: dayjs()
  });

  const formatDateTime = (value?: Date | string) => {
    if (!value) return '-';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '-';
  };

  const getServiceStartAt = (trip: ParkingBillTrip) => {
    return trip?.startedAt || trip?.startAt || trip?.scheduleAt;
  };

  const loadParkingBills = async (page: number = 1) => {
    setLoading(true);
    try {
      const startDate = dateRange?.startDate?.format('YYYY-MM-DD');
      const endDate = dateRange?.endDate?.format('YYYY-MM-DD');
      const result = await getTripsParkingReport(page, pagination.pageSize, startDate, endDate, status);
      setParkingBills(result.data);
      setPagination(prev => ({
        ...prev,
        current: result.currentPage,
        total: result.total,
      }));
    } catch (error) {
      message.error('Error loading parkingBills');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (parkingId: number, status: string, comment: string) => {
    setUpdating(parkingId);
    try {
      await updateTripParkingStatus(parkingId, status, comment);
      message.success(`Estado actualizado exitosamente a: ${status}`);
      await loadParkingBills(pagination.current);
    } catch (error) {
      message.error('Error al actualizar el estado');
    } finally {
      setUpdating(null);
    }
  };

  const handleViewComprobante = (url: string) => {
    if (!url) {
      message.error('No hay comprobante disponible');
      return;
    }
    
    setCurrentImageUrl(url);
    setImageModalVisible(true);
  };

  const showActionModal = (bill: ParkingBill, action: 'approved' | 'rejected') => {
    setSelectedBill(bill);
    setSelectedAction(action);
    setComment('');
    setActionModalVisible(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedBill || !selectedAction) return;

    try {
      await handleUpdateStatus(selectedBill.id, selectedAction, comment);
      setActionModalVisible(false);
      setSelectedBill(null);
      setSelectedAction('');
      setComment('');
    } catch (error) {
      
    }
  };

  const handleCancelAction = () => {
    setActionModalVisible(false);
    setSelectedBill(null);
    setSelectedAction('');
    setComment('');
  };

  const handleExport = async () => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      message.error('Please select date range');
      return;
    }

    const diffInDays = dateRange?.endDate.diff(dateRange?.startDate, 'day');
    if (diffInDays > 31) {
      message.error('Date range cannot exceed 31 days');
      return;
    }

    setExporting(true);
    try {
      const startDate = dateRange?.startDate.format('YYYY-MM-DD');
      const endDate = dateRange?.endDate.format('YYYY-MM-DD');
      let data: ParkingBill[] = [];
      let withData = true;
      while (withData) {
        const request = await getTripsParkingReport(1, 50, startDate, endDate, status);
        data = [...data, ...request?.data]
        withData = request?.data?.length && request.total > data.length;        
      }
      const worksheet = XLSX.utils.json_to_sheet(data.map(bill => ({
        Id: bill.id,
        Conductor: `${bill?.trip?.provider?.firstName} ${bill?.trip?.provider?.lastName}`,
        Monto: bill.details?.amount,
        Estado: bill.status,
        'Actualizado Por': bill.statusUpdatedBy || '',
        Parking: bill.trip?.originAddress,
        'Inicio servicio': formatDateTime(getServiceStartAt(bill.trip)),
        Origen: bill.trip?.originAddress,
        Destino: bill.trip?.destinationAddress,
        'Finalizado': formatDateTime(bill.trip?.finishedAt),
        'Comentario': bill.details?.comment || '',
      })));
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'parkingBills');
      XLSX.writeFile(workbook, `parkingBills-${startDate}-to-${endDate}.xlsx`);
      
      message.success('Export completed successfully');
    } catch (error) {
      message.error('Error exporting parkingBills');
    } finally {
      setExporting(false);
    }
  };

  const handleSearch = () => {
    if (dateRange?.startDate && dateRange?.endDate) {
      const diffInDays = dateRange?.endDate.diff(dateRange?.startDate, 'day');
      if (diffInDays > 31) {
        message.error('El rango no puede exceder los 31 días');
        return;
      }
    }
    loadParkingBills(1);
  };

  const handleTableChange = (newPagination: any) => {
    loadParkingBills(newPagination.current);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Conductor',
      dataIndex: 'provider',
      render: (_: string, {trip}: ParkingBill) => `${trip?.provider?.firstName} ${trip?.provider?.lastName}`,
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      render: (_: string, {details}: ParkingBill) => details?.amount,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'approved' ? 'green' : status === 'rejected' ? 'red' : 'blue'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actualizado por',
      dataIndex: 'statusUpdatedBy',
      key: 'statusUpdatedBy',
    },
    {
      title: 'Comentarios',
      dataIndex: 'comment',
      key: 'comment',
      render: (_: string, {details}: ParkingBill) => details?.comment || '',
    },
    {
      title: 'Inicio servicio',
      dataIndex: 'startAt',
      key: 'startAt',
      render: (_: string, {trip}: ParkingBill) => formatDateTime(getServiceStartAt(trip)),
    },
    {
      title: 'Origen',
      dataIndex: 'originAddress',
      key: 'originAddress',
      render: (_: string, {trip}: ParkingBill) => trip?.originAddress,
    },
    {
      title: 'Destino',
      dataIndex: 'destinationAddress',
      key: 'destinationAddress',
      render: (_: string, {trip}: ParkingBill) => trip?.destinationAddress,
    },
    {
      title: 'Finalizado',
      dataIndex: 'finishedAt',
      key: 'finishedAt',
      render: (_: string, {trip}: ParkingBill) => formatDateTime(trip?.finishedAt),
    },
    {
      title: 'Comprobante',
      key: 'comprobante',
      width: 100,
      render: (_: string, record: ParkingBill) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleViewComprobante(record.url)}
          disabled={!record.url}
          title="Ver comprobante"
        >
          Ver
        </Button>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      render: (_: string, record: ParkingBill) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<CheckOutlined />}
            size="small"
            loading={updating === record.id}
            onClick={() => showActionModal(record, 'approved')}
            disabled={record.status === 'approved'}
          >
            Aprobar
          </Button>
          <Button
            danger
            icon={<CloseOutlined />}
            size="small"
            loading={updating === record.id}
            onClick={() => showActionModal(record, 'rejected')}
            disabled={record.status === 'rejected'}
          >
            Rechazar
          </Button>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadParkingBills(1);
  }, []);

  const getActionText = () => {
    return selectedAction === 'approved' ? 'Aprobar' : 'Rechazar';
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={6} md={4}>
            <Form.Item label="Estado" name="state">
              <Select onChange={setStatus} value={status} defaultValue='pending' >
                <Select.Option value="">Todos</Select.Option>
                <Select.Option value="pending">Pendientes</Select.Option>
                <Select.Option value="approved">Aprobados</Select.Option>
                <Select.Option value="rejected">Rechazados</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <RangePicker
              style={{ width: '100%' }}
              value={[dateRange.startDate, dateRange.endDate]}
              onChange={(dateRange) => {
                if (dateRange && dateRange[0] && dateRange[1]) {
                  setDateRange({
                    startDate: dateRange[0], 
                    endDate: dateRange[1]
                  });
                }
              }}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={handleSearch} loading={loading}>
                Buscar
              </Button>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleExport}
                loading={exporting}
              >
                Descargar
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={parkingBills}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          rowKey="id"
        />
      </Card>

      <Modal
        title="Comprobante de Parking"
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setImageModalVisible(false)}>
            Cerrar
          </Button>,
          <Button 
            key="download" 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={() => {
              const link = document.createElement('a');
              link.href = currentImageUrl;
              link.target = '_blank';
              link.download = `comprobante-parking-${new Date().getTime()}`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Descargar
          </Button>
        ]}
        width={800}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {currentImageUrl ? (
            <Image
              src={currentImageUrl}
              alt="Comprobante de parking"
              style={{ maxWidth: '100%', maxHeight: '500px' }}
              preview={false}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
              onError={() => {
                message.warning('No se pudo cargar la imagen. Intentando abrir en nueva pestaña...');
                window.open(currentImageUrl, '_blank');
              }}
            />
          ) : (
            <p>No hay comprobante disponible</p>
          )}
        </div>
      </Modal>

      <Modal
        title={`Confirmar ${getActionText().toLowerCase()}`}
        open={actionModalVisible}
        onOk={handleConfirmAction}
        onCancel={handleCancelAction}
        okText={getActionText()}
        cancelText="Cancelar"
        okButtonProps={{
          danger: selectedAction === 'rejected',
          type: 'primary',
          loading: updating === selectedBill?.id,
        }}
        confirmLoading={updating === selectedBill?.id}
      >
        {selectedBill && (
          <div>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Conductor">
                {`${selectedBill.trip.provider.firstName} ${selectedBill.trip.provider.lastName}`}
              </Descriptions.Item>
              <Descriptions.Item label="Booking ID">{selectedBill.trip.bookingId}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedBill.trip.provider.email}</Descriptions.Item>
              <Descriptions.Item label="Monto">{selectedBill.details?.amount || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Origen">{selectedBill.trip.originAddress}</Descriptions.Item>
              <Descriptions.Item label="Destino">{selectedBill.trip.destinationAddress}</Descriptions.Item>
            </Descriptions>
            <Form.Item label="Comentario (opcional)">
              <TextArea
                rows={3}
                placeholder="Ingrese un comentario..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Form.Item>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TripParkingView;